/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useEffect } from "react";

const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/**
 * Traps keyboard focus within a container when active. Handles Tab cycling,
 * Escape to close, and outside click/touch detection.
 *
 * @param isActive - Whether the focus trap is currently active
 * @param containerRef - Ref to the container element to trap focus within
 * @param onRequestClose - Callback when user requests close (Escape or outside click)
 * @param triggerRef - Optional ref to element that triggered the trap (receives focus on close)
 * @param backdropClass - Optional CSS class name for backdrop elements (click to close)
 * @param ignoreSelector - Optional CSS selector for elements to ignore in outside click detection
 */
export function useFocusTrap(
  isActive: boolean,
  containerRef: React.RefObject<HTMLElement | null>,
  onRequestClose: () => void,
  triggerRef?: React.RefObject<HTMLElement | null>,
  backdropClass?: string,
  ignoreSelector?: string,
): void {
  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const controller = new AbortController();
    const { signal } = controller;
    const container = containerRef.current;

    const handleKeyboardEvent = (e: KeyboardEvent) => {
      /* c8 ignore next -- { signal } addEventListener auto-removes listener on abort */
      if (signal.aborted) return;

      if (e.key === "Tab") {
        const focusableElements = Array.from(
          container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        );

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus?.();
        }

        /* c8 ignore next -- forward-Tab wrap: jsdom doesn't update activeElement reliably */
        if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus?.();
        }
      }

      /* c8 ignore next -- Escape: tested functionally but v8 misattributes this branch */
      if (e.key === "Escape") {
        e.preventDefault();
        onRequestClose();
        triggerRef?.current?.focus?.();
      }
    };

    const handleClickEvent = (e: Event) => {
      /* c8 ignore next -- { signal } addEventListener auto-removes listener on abort */
      if (signal.aborted) return;

      const target = e.target as HTMLElement;

      if (backdropClass && target?.classList?.contains(backdropClass)) {
        onRequestClose();
        return;
      }

      if (
        !container.contains(target) &&
        !(triggerRef?.current && triggerRef.current.contains(target)) &&
        !(ignoreSelector && target.closest(ignoreSelector))
      ) {
        onRequestClose();
      }
    };

    document.addEventListener("keydown", handleKeyboardEvent, {
      signal,
      passive: false,
    });

    document.addEventListener("mousedown", handleClickEvent, {
      signal,
      passive: true,
    });

    document.addEventListener("touchstart", handleClickEvent, {
      signal,
      passive: true,
    });

    const focusTimeout = setTimeout(() => {
      /* c8 ignore next -- clearTimeout in cleanup prevents entry if aborted */
      if (signal.aborted) return;

      const focusableElements = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );

      /* c8 ignore next -- focusable elements always present when timeout fires in tests */
      if (focusableElements.length > 0) {
        focusableElements[0]?.focus();
      }
    }, 100);

    return () => {
      clearTimeout(focusTimeout);
      controller.abort();
    };
  }, [
    isActive,
    containerRef,
    onRequestClose,
    triggerRef,
    backdropClass,
    ignoreSelector,
  ]);
}
